import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  Bot,
  ChevronsUpDown,
  LogOut,
  Monitor,
  Moon,
  Sun,
  UserRound,
} from "lucide-react";

import { cn } from "@base/ui";
import {
  aplicarTemaRenke,
  escolherTemaRenke,
  temaRenkeSalvo,
  type TemaRenke,
} from "@base/ui/renke";
import { useAuth } from "../../../shared/auth/auth.contexto";

/**
 * Rodapé sidebar-07: conta enxuta (perfil, tema, sair).
 * Referencias e Usuarios ficam na navegacao lateral.
 */
export function RodapeDaSessao({
  soIcone = false,
}: {
  readonly soIcone?: boolean;
}) {
  const { sair, sessao } = useAuth();
  const [aberto, definirAberto] = useState(false);
  const [tema, definirTema] = useState<TemaRenke>(() =>
    typeof document === "undefined" ? "light" : temaRenkeSalvo(),
  );
  const raiz = useRef<HTMLDivElement>(null);
  const idMenu = useId();
  const nomeConta =
    sessao?.nome?.trim() || sessao?.cliente?.trim() || "Creative OS";
  const emailConta = sessao?.email?.trim() || null;
  const inicial = nomeConta.slice(0, 1).toUpperCase();

  useEffect(() => {
    aplicarTemaRenke(tema);
  }, [tema]);

  useEffect(() => {
    if (tema !== "system") return;
    const consulta = globalThis.matchMedia?.("(prefers-color-scheme: dark)");
    if (!consulta) return;
    const reagir = () => aplicarTemaRenke("system");
    consulta.addEventListener("change", reagir);
    return () => consulta.removeEventListener("change", reagir);
  }, [tema]);

  useEffect(() => {
    if (!aberto) return;
    const fecharFora = (evento: MouseEvent) => {
      if (!raiz.current?.contains(evento.target as Node)) {
        definirAberto(false);
      }
    };
    const fecharTecla = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") definirAberto(false);
    };
    document.addEventListener("mousedown", fecharFora);
    document.addEventListener("keydown", fecharTecla);
    return () => {
      document.removeEventListener("mousedown", fecharFora);
      document.removeEventListener("keydown", fecharTecla);
    };
  }, [aberto]);

  function irPara(hash: string) {
    definirAberto(false);
    window.location.hash = hash;
  }

  return (
    <div ref={raiz} className="relative w-full">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={idMenu}
        title="Conta e preferências"
        aria-label="Conta e preferências"
        onClick={() => definirAberto((v) => !v)}
        className={cn(
          "flex w-full cursor-pointer items-center gap-2 rounded-lg text-left transition-colors duration-200",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          soIcone ? "size-9 justify-center p-0" : "gap-2.5 px-2 py-2",
          aberto && "bg-sidebar-accent text-sidebar-accent-foreground",
        )}
      >
        <span
          className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary text-xs font-semibold tracking-tight text-primary-foreground"
          aria-hidden="true"
        >
          {inicial}
        </span>
        {!soIcone ? (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold tracking-tight text-sidebar-foreground">
                {nomeConta}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {emailConta ?? "Creative OS"}
              </span>
            </span>
            <ChevronsUpDown
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </>
        ) : null}
      </button>

      {aberto ? (
        <div
          id={idMenu}
          role="menu"
          aria-label="Conta e preferências"
          className={cn(
            "absolute z-50 mb-2 min-w-[14rem] overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lift",
            soIcone ? "bottom-full left-0" : "bottom-full left-0 right-0",
          )}
        >
          <div className="border-b border-border px-2.5 py-2">
            <p className="truncate text-sm font-semibold tracking-tight">
              {nomeConta}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {emailConta ?? "Sessão nesta aba"}
            </p>
          </div>

          <div className="py-1" role="group" aria-label="Conta">
            <BotaoMenu
              icone={<UserRound className="size-4 shrink-0" aria-hidden="true" />}
              rotulo="Configurações da conta"
              onClick={() => irPara("perfil")}
            />
            <BotaoMenu
              icone={<Bot className="size-4 shrink-0" aria-hidden="true" />}
              rotulo="Conectar IA (MCP)"
              onClick={() => irPara("mcp")}
            />
          </div>

          <div
            className="border-t border-border py-1"
            role="group"
            aria-label="Tema"
          >
            <p className="px-2.5 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Tema
            </p>
            {(
              [
                { tema: "light" as const, rotulo: "Claro", Icone: Sun },
                { tema: "dark" as const, rotulo: "Escuro", Icone: Moon },
                { tema: "system" as const, rotulo: "Sistema", Icone: Monitor },
              ] as const
            ).map(({ tema: opcao, rotulo, Icone }) => (
              <button
                key={opcao}
                type="button"
                role="menuitemradio"
                aria-checked={tema === opcao}
                onClick={() => {
                  escolherTemaRenke(opcao);
                  definirTema(opcao);
                }}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm transition-colors",
                  tema === opcao
                    ? "bg-accent text-accent-foreground"
                    : "text-popover-foreground hover:bg-muted",
                )}
              >
                <Icone className="size-4 shrink-0" aria-hidden="true" />
                {rotulo}
              </button>
            ))}
          </div>

          <div className="border-t border-border p-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                definirAberto(false);
                void sair();
              }}
              className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-popover-foreground transition-colors hover:bg-muted"
            >
              <LogOut className="size-4 shrink-0" aria-hidden="true" />
              Sair
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BotaoMenu({
  icone,
  rotulo,
  onClick,
}: {
  readonly icone: ReactNode;
  readonly rotulo: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-popover-foreground transition-colors hover:bg-muted"
    >
      {icone}
      {rotulo}
    </button>
  );
}

export function rodapeDaSessaoNaLateral({
  soIcone,
}: {
  readonly soIcone: boolean;
}) {
  return (
    <div className="space-y-1">
      <RodapeDaSessao soIcone={soIcone} />
      {!soIcone ? (
        <p className="px-2 text-[11px] text-sidebar-foreground/40">
          v{__APP_VERSION__}
        </p>
      ) : null}
    </div>
  );
}
